import React from 'react';
import { noop } from 'utils/noop';
import { Table } from '@landr/maestro';
import { Trans } from '@lingui/macro';
import { Sample } from 'types/generated/graphql';
import VerboseError from 'error/components/VerboseError';
import { eventSubscription } from 'helpers/tableEvent/tableEvent';
import { SamplesTableSortingTypes } from 'components/DetailsView/components/useSortingOptionItem';
import { SamplesTableRow } from './SamplesTableRow/SamplesTableRow';
import SelectAllCheckbox from './SelectAllCheckbox';
import {
    samplesTable,
    waveformCell,
    nameCell,
    actionsGroupCellStyles,
    playButtonCell,
    informationsCell,
} from './SamplesTable.styled';
import { useSamplesTable } from './useSamplesTable';
import SamplesCell from './SamplesTableRow/components/SamplesCell/SamplesCell';

const SkeletonRow = ({
    isSm,
    isMd,
    isMl,
    isLg,
    nbOfItems,
}: {
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
    nbOfItems: number;
}) => {
    const dataPlaceholder: Array<React.ReactElement> = [];

    for (let i = 0; i < nbOfItems; i++) {
        dataPlaceholder.push(
            <SamplesTableRow
                rowIndex={i}
                key={i}
                isLoading={true}
                onClick={noop}
                isSm={isSm}
                isMd={isMd}
                isMl={isMl}
                isLg={isLg}
            />,
        );
    }

    return <>{dataPlaceholder}</>;
};

export interface SamplesTableHeadProps {
    samples: Sample[];
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
}

const TableHead: React.FC<SamplesTableHeadProps> = ({
    samples,
    isSm,
    isMd,
    isMl,
    isLg,
}) => {
    return (
        <Table.Head>
            <Table.Row>
                <SamplesCell width="xs" isHidden={isSm}>
                    <SelectAllCheckbox samples={samples} />
                </SamplesCell>
                <SamplesCell width="sm" isHidden={isSm}>
                    <Trans>Pack</Trans>
                </SamplesCell>
                <SamplesCell className={playButtonCell} isHidden={isSm} />
                <SamplesCell
                    className={nameCell}
                    isSortable={isSm ? false : true}
                    sortingType={SamplesTableSortingTypes.Name}
                >
                    {isSm ? <></> : <Trans>Name</Trans>}
                </SamplesCell>
                <SamplesCell className={waveformCell} isHidden={isMl} />
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                    isSortable
                    sortingType={SamplesTableSortingTypes.Time}
                >
                    <Trans>Time</Trans>
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                    isSortable
                    sortingType={SamplesTableSortingTypes.Type}
                >
                    Type
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({ isLargeDesktop: !isLg })}
                    isHidden={isMd}
                    isSortable
                    sortingType={SamplesTableSortingTypes.Bpm}
                >
                    <Trans>Bpm</Trans>
                </SamplesCell>
                <SamplesCell
                    className={informationsCell({
                        isLargeDesktop: !isLg,
                        isKey: true,
                    })}
                    isHidden={isMd}
                    isSortable
                    sortingType={SamplesTableSortingTypes.Key}
                >
                    <Trans>Key</Trans>
                </SamplesCell>
                <SamplesCell
                    className={actionsGroupCellStyles({ isLg, isMd, isSm })}
                />
            </Table.Row>
        </Table.Head>
    );
};

export type SamplesTableProps = {
    items: Sample[];
    itemsPerPage: number;
    isFetching: boolean;
    error?: unknown;
    isSm: boolean;
    isMd: boolean;
    isMl: boolean;
    isLg: boolean;
    onPageChanged: () => void;
};

export const SamplesTable: React.FC<SamplesTableProps> = ({
    items,
    itemsPerPage,
    isFetching,
    error,
    isSm,
    isMd,
    isMl,
    isLg,
    onPageChanged,
}): JSX.Element => {
    const { handleRowChange, focusedRowIndexRef, isFetchingDebounced, page } =
        useSamplesTable({
            itemsPerPage,
            isFetching,
            onPageChanged,
        });

    if (error) {
        return <VerboseError />;
    }

    return (
        <>
            <Table
                className={samplesTable}
                listenKeyEvents
                onRowChange={handleRowChange}
                focusedRowIndexRef={focusedRowIndexRef}
            >
                <TableHead
                    samples={items}
                    isSm={isSm}
                    isMd={isMd}
                    isMl={isMl}
                    isLg={isLg}
                />
                <Table.Body data-testid="samples-table-body">
                    {(!items.length || isFetchingDebounced) && (
                        <SkeletonRow
                            isSm={isSm}
                            isMd={isMd}
                            isMl={isMl}
                            isLg={isLg}
                            nbOfItems={itemsPerPage}
                        />
                    )}
                    {!isFetchingDebounced &&
                        items.map((sample: Sample, index: number) => {
                            return (
                                <SamplesTableRow
                                    page={page}
                                    key={sample.guid}
                                    rowIndex={index}
                                    sample={sample}
                                    isLoading={isFetchingDebounced}
                                    eventSubscription={eventSubscription}
                                    isSm={isSm}
                                    isMd={isMd}
                                    isMl={isMl}
                                    isLg={isLg}
                                />
                            );
                        })}
                </Table.Body>
            </Table>
        </>
    );
};

export default SamplesTable;
